/*
 * (c) Copyright 2024 Calabrio, Inc.
 * All Rights Reserved. www.calabrio.com LICENSED MATERIALS
 * Property of Calabrio, Inc., Minnesota, USA
 *
 * No part of this publication may be reproduced, stored or transmitted,
 * in any form or by any means (electronic, mechanical, photocopying,
 * recording or otherwise) without prior written permission from Calabrio, Inc.
 */

import Vue from 'vue';
import axios from 'axios';
import hash from 'object-hash';

export default {
  API: {
    //interactionsAnalytics
    INTERACTIONS_CHAT_FILTERS: '/interactionsAnalytics/chatFilters',
    INTERACTIONS_CHAT_TOPICS: '/interactionsAnalytics/chatTopics',
    INTERACTIONS_ALL_CONTACT_REASONS: '/interactionsAnalytics/allContactReasons',
    INTERACTIONS_CHAT_PERFORMANCE: '/interactionsAnalytics/chatPerformance',
    INTERACTIONS_CONTACT_REASONS: '/interactionsAnalytics/reasonsForContact',
    INTERACTIONS_CONTACT_REASONS_DETAIL: '/interactionsAnalytics/reasonsForContactDetail',
    INTERACTIONS_CONTACT_REASONS_TOPIC_TREMD: '/interactionsAnalytics/topicTrend',
    INTERACTIONS_CONTACT_REASONS_TOPIC_ENTITIES: '/interactionsAnalytics/topicEntities',
    INTERACTIONS_CONTACT_REASONS_GROUPED_TOPIC_CONVERSATION: '/interactionsAnalytics/groupedTopicConversation',
    INTERACTIONS_CONTACT_REASONS_RELATED_TOPIC_CONVERSATION: '/interactionsAnalytics/relatedTopicConversation',
    INTERACTIONS_CONTACT_REASONS_TOPIC_CONVERSATION_BY_CONFIDENCE: '/interactionsAnalytics/topicsByConfidenceScore',
    INTERACTIONS_CONTACT_REASONS_TOPIC_CONVERSATION_DETAIL: '/interactionsAnalytics/topicConversationDetail',
    INTERACTIONS_CONTACT_REASONS_ALL_TOPICS: '/interactionsAnalytics/allTopics',
    INTERACTIONS_CONTACT_REASONS_SAVE_NEW_TOPICS: '/interactionsAnalytics/saveTopicsToTask',
    INTERACTIONS_CONTACT_REASONS_MERGE_TOPICS: '/interactionsAnalytics/mergeTopics',
    INTERACTIONS_CONTACT_REASONS_TOPIC_UPDATETOPICNAME: '/interactionsAnalytics/renameTopic',

    INTERACTIONS_METRICS: '/interactionsAnalytics/metrics',
    INTERACTIONS_TRANSCRIPT_GET_TIME: '/interactionsAnalytics/getTranscriptSearchFilters',
    INTERACTIONS_TRANSCRIPT_SEARCH: '/interactionsAnalytics/getTranscripts',
    INTERACTIONS_TOPIC_DISPLAY_NAME_LIST: '/interactionsAnalytics/getTopicDisplayNameList',
    INTERACTIONS_RELATED_TOPIC_LIST: '/interactionsAnalytics/getRelatedTopicList',
    INTERACTIONS_INTENT_NAME_LIST: '/interactionsAnalytics/getIntentNames',
    INTERACTIONS_TRANSCRIPT_SUMMARY: '/interactionsAnalytics/getTranscriptContentSummary',
    INTERACTIONS_TRANSCRIPT_EXPORT: '/interactionsAnalytics/exportTranscripts',
    INTERACTIONS_ADD_TRANSCRIPT: '/interactionsAnalytics/addTranscript',
    INTERACTIONS_REMOVE_TRANSCRIPT: '/interactionsAnalytics/removeTranscript',
    INTERACTIONS_CONTACT_REASONS_LIST: '/interactionsAnalytics/getContactReasons',

    INTERACTIONS_DASHBOARD_RELATED_TOPICS: '/interactionsAnalytics/relatedTopics',
  },

  cachedHash: {},
  cancelTokens: [],

  getRequest(authUser, url, headers) {
    if (authUser.accessToken) {
      if (headers == null) headers = {};
      headers['Authorization'] = 'Bearer ' + authUser.accessToken;
      headers['Api-Configs'] = JSON.stringify(authUser.config.api.configs);
      if(authUser.sessionId) {
        headers['Auth-Host'] = authUser.authHost || "";
        headers['Auth-Session-Id'] = authUser.sessionId;
        headers['Auth-Product-Name'] = authUser.productName || "";
        headers['Auth-Options'] = authUser.authOptions || "";
      }

      return axios.get(url, { headers: headers, timeout: 300000 });
    } else {
      return null;
    }
  },

  postRequest(authUser, url, payload, headers) {
    if (authUser.accessToken) {
      if (headers == null) headers = {};
      headers['Authorization'] = 'Bearer ' + authUser.accessToken;
      headers['Api-Configs'] = JSON.stringify(authUser.config.api.configs);
      if(authUser.sessionId) {
        headers['Auth-Host'] = authUser.authHost || "";
        headers['Auth-Session-Id'] = authUser.sessionId;
        headers['Auth-Product-Name'] = authUser.productName || "";
        headers['Auth-Options'] = authUser.authOptions || "";
      }

      let cancelTokenKey = url;
      if (this.cancelTokens[cancelTokenKey]) {
        // cancel request
        this.cancelTokens[cancelTokenKey].cancel();
      }
      this.cancelTokens[cancelTokenKey] = axios.CancelToken.source();

      let options = {
        timeout: 300000, // timeout in 5 mins
        headers: headers,
        cancelToken: this.cancelTokens[cancelTokenKey].token,
      };

      return axios.post(url, payload, options);
    } else {
      return null;
    }
  },

  fetchChatTopics(authUser, payload) {
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_CHAT_TOPICS;
    return this.postRequest(authUser, url, payload);
  },

  fetchAllContactReasons(authUser, payload) {
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_ALL_CONTACT_REASONS;
    return this.postRequest(authUser, url, payload);
  },

  fetchChatFilters(authUser) {
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_CHAT_FILTERS;
    return this.getRequest(authUser, url);
  },

  fetchChatPerformance(authUser, payload) {
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_CHAT_PERFORMANCE;
    return this.postRequest(authUser, url, payload);
  },

  fetchContactReasons(authUser, options, payload) {
    delete payload.topics;

    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_CONTACT_REASONS;
    let hashName = `fetchContactReasons`;
    let payloadHash = hash(payload);
    if ((options && options.forceRefresh) || this.cachedHash[hashName] != payloadHash) {
      this.cachedHash[hashName] = payloadHash;
      return new Promise(async (resolve) => {
        let res = await this.postRequest(authUser, url, payload);
        resolve(res);
      });
    } else {
      return new Promise(async (resolve) => {
        resolve(true);
      });
    }
  },
  
  fetchAllTopics(authUser, payload) {
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_CONTACT_REASONS_ALL_TOPICS;
    return this.postRequest(authUser, url, payload);
  },

  saveTopicsToTask(authUser, payload) {
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_CONTACT_REASONS_SAVE_NEW_TOPICS;
    return this.postRequest(authUser, url, payload);
  },

  mergeContactReasonsTopics(authUser, payload) {
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_CONTACT_REASONS_MERGE_TOPICS;
    return this.postRequest(authUser, url, payload);
  },

  updateTopicName(authUser, topicNameObject) {
    let payload = {
      newTopicDisplayName: topicNameObject.newTopicDisplayName,
      oldTopicDisplayName: topicNameObject.oldTopicDisplayName,
      taskId: topicNameObject.taskId,
      topicsToMerge: topicNameObject.topicsToMerge,
      updatedBy: authUser.user.email,
    };
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_CONTACT_REASONS_TOPIC_UPDATETOPICNAME;
    return this.postRequest(authUser, url, payload);
  },

  fetchContactReasonsTopicTrend(user, payload) {
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_CONTACT_REASONS_TOPIC_TREMD;
    return this.postRequest(user, url, payload);
  },

  fetchContactReasonsTopicEntities(authUser, payload) {
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_CONTACT_REASONS_TOPIC_ENTITIES + '?t=e';
    return this.postRequest(authUser, url, payload);
  },

  fetchContactReasonsGroupedTopicConversations(authUser, payload) {
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_CONTACT_REASONS_GROUPED_TOPIC_CONVERSATION;
    delete payload.from;
    return this.postRequest(authUser, url, payload);
  },

  fetchContactReasonsRelatedTopicConversations(authUser, payload) {
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_CONTACT_REASONS_RELATED_TOPIC_CONVERSATION;
    delete payload.from;
    return this.postRequest(authUser, url, payload);
  },

  fetchTopicsByConfidenceScore(authUser, payload) {
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_CONTACT_REASONS_TOPIC_CONVERSATION_BY_CONFIDENCE;
    delete payload.from;
    return this.postRequest(authUser, url, payload);
  },

  fetchContactReasonsTopicConversationDetail(authUser, payload) {
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_CONTACT_REASONS_TOPIC_CONVERSATION_DETAIL;
    return this.postRequest(authUser, url, payload);
  },

  fetchContactReasonList(authUser, payload) {
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_CONTACT_REASONS_LIST;
    return this.postRequest(authUser, url, payload);
  },

  fetchTranscriptSearchTimeRange(authUser, payload) {
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_TRANSCRIPT_GET_TIME;
    return this.postRequest(authUser, url, payload);
  },
  fetchTranscriptSearch(authUser, payload) {
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_TRANSCRIPT_SEARCH;
    return this.postRequest(authUser, url, payload);
  },
  fetchTopicDisplayNameList(authUser, payload) {
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_TOPIC_DISPLAY_NAME_LIST;
    return this.postRequest(authUser, url, payload);
  },
  fetchTranscriptSummary(authUser, payload) {
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_TRANSCRIPT_SUMMARY;
    return this.postRequest(authUser, url, payload);
  },

  addTranscript(authUser, payload) {
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_ADD_TRANSCRIPT;
    return this.postRequest(authUser, url, payload);
  },

  removeTranscript(authUser, payload) {
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_REMOVE_TRANSCRIPT;
    return this.postRequest(authUser, url, payload);
  },

  exportTranscriptSearch(authUser, payload) {
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_TRANSCRIPT_EXPORT;
    let headers = {};
    headers['Authorization'] = 'Bearer ' + authUser.accessToken;
    headers['Api-Configs'] = JSON.stringify(authUser.config.api.configs);
    if(authUser.sessionId) {
      headers['Auth-Host'] = authUser.authHost || "";
      headers['Auth-Session-Id'] = authUser.sessionId;
      headers['Auth-Product-Name'] = authUser.productName || "";
      headers['Auth-Options'] = authUser.authOptions || "";
    }

    let cancelTokenKey = url;
    if (this.cancelTokens[cancelTokenKey]) {
      // cancel request
      this.cancelTokens[cancelTokenKey].cancel();
    }
    this.cancelTokens[cancelTokenKey] = axios.CancelToken.source();

    let options = {
      timeout: 300000, // timeout in 5 mins
      headers: headers,
      cancelToken: this.cancelTokens[cancelTokenKey].token,
    };
    return axios.post(url, payload, options).then((response) => {
      var fileURL = window.URL.createObjectURL(new Blob([response.data]));
      var fileLink = document.createElement('a');
      fileLink.href = fileURL;
      fileLink.setAttribute('download', 'transcripts.csv');
      document.body.appendChild(fileLink);
      fileLink.click();
    });
  },

  fetchMetrics(user, payload) {
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_METRICS;
    return this.postRequest(user, url, payload);
  },

  fetchRelatedTopics(authUser, payload) {
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.INTERACTIONS_DASHBOARD_RELATED_TOPICS;
    delete payload.from;
    return this.postRequest(authUser, url, payload);
  },
};
